import '../App.css';
import { useParams } from 'react-router-dom';
import { Alert, Heading, Flex, Button } from "@aws-amplify/ui-react";
import CreditNoteForm from "../ui-components/CreditNoteForm";
import LineItemsDataGrid from "../components/LineItemsDataGrid";
import OrdersDataGrid from "../components/OrdersDataGrid";
import FileUploadDownload from "../components/FileUploadDownload";
import { useState, useEffect } from 'react';
import { API, Auth, graphqlOperation } from "aws-amplify";
import { v4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import { generateCreditNoteNumber } from '../utils/generateCreditNoteNumber';
import { formatAWSDate } from '../utils/formatAWSDate';

export default function EditCreditNote() {
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [creditNote, setCreditNote] = useState();
  const [originalCreditNote, setOriginalCreditNote] = useState();
  const [orders, setOrders] = useState();
  const [lineItems, setLineItems] = useState([]);
  const [originalLineItems, setOriginalLineItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [folder, setFolder] = useState();

  const navigate = useNavigate();

  const addAlert = (alertMessage) => {
    setAlerts(prevAlerts => [...prevAlerts, 
      <Alert key={v4()} isDismissible={true} variation="error">{alertMessage}</Alert>
    ]);
  }

  async function fetchAllLineItems(creditNoteId) {
    let nextToken = null;
    let allLineItems = [];
  
    do {
      const response = await API.graphql(graphqlOperation(queries.lineItemsByCreditNote, { 
        creditNoteLine_itemsId: creditNoteId,
        limit: 1000,
        nextToken: nextToken
      }));
      
      // Check for GraphQL errors
      if (response.errors && response.errors.length > 0) {
        console.error('Fetch line items GraphQL errors:', response.errors);
        throw new Error(`Failed to fetch line items: ${response.errors[0].message}`);
      }
  
      allLineItems = allLineItems.concat(response.data.lineItemsByCreditNote.items);
      nextToken = response.data.lineItemsByCreditNote.nextToken;
    } while (nextToken);
  
    return allLineItems;
  }

  useEffect(() => {
    const getCreditNote = async () => {
      setIsLoading(true);
      try {
        const creditNoteResponse = await API.graphql(graphqlOperation(queries.getCreditNote, { id: id }));
        
        // Check for GraphQL errors
        if (creditNoteResponse.errors && creditNoteResponse.errors.length > 0) {
          console.error('Get credit note GraphQL errors:', creditNoteResponse.errors);
          addAlert(`Failed to load credit note: ${creditNoteResponse.errors[0].message}`);
          return;
        }
        
        const creditNote = creditNoteResponse.data.getCreditNote;
        if (creditNote == null) {
          addAlert("Cannot find a credit note with ID " + id);
          return;
        }
        
        const originalLineItems = await fetchAllLineItems(creditNote.id);
        setCreditNote(creditNote);
        setFolder(creditNote.folder || creditNote.id);
        setOriginalCreditNote(creditNote); // the onchange method removes any info not on the form, keep the original record here
        setOrders([creditNote]);
        setOriginalLineItems(originalLineItems);
        // convert line items to a mutable version
        const lineItems = originalLineItems.map(originalLineItem => ({
          id: originalLineItem.id,
          sku: originalLineItem.sku,
          item_name: originalLineItem.item_name,
          use_by_date: originalLineItem.use_by_date,
          quantity_ordered: originalLineItem.quantity_ordered,
          quantity_to_credit: originalLineItem.quantity_to_credit,
          original_unit_price: originalLineItem.original_unit_price?.toFixed(2),
          unit_price: originalLineItem.unit_price?.toFixed(2)
        }));
        setLineItems(lineItems);
        setAlerts([]);
      } catch (error) {
        console.log(error);
        addAlert(error.toString());
      } finally {
        setIsLoading(false);
      }
    }
    getCreditNote();
  }, [id]);

  const creditNoteValidation = () => {
    let validation_error = null;
    if (!creditNote.comments) {
      lineItems.forEach(async lineItem => {
        if (lineItem.unit_price > lineItem.original_unit_price) {
          validation_error = 'Reason comments must be entered if unit price is greater than original unit price';
        }
        if (lineItem.quantity_to_credit > lineItem.quantity_ordered) {
          validation_error = 'Reason comments must be entered if quantity to credit is greater than quantity ordered';
        }
      });
    }
    return validation_error;
  }

  const updateCreditNote = async () => {
    setIsSaving(true);
    try {
      // Validate credit note before submission
      const validation_error = creditNoteValidation();
      if (validation_error) {
        addAlert(validation_error);
        setIsSaving(false);
        return;
      }
      
      const currentUser = await Auth.currentUserInfo();
      if (!currentUser) {
        // not logged in, force a login
        window.location.reload();
        return;
      }
      
      let credit_note_number;
      if (originalCreditNote.credit_note_number) {
        credit_note_number = originalCreditNote.credit_note_number;
      } else {
        credit_note_number = await generateCreditNoteNumber(originalCreditNote.order_number);
      }
      
      const updatedCreditNote = {
        id: originalCreditNote.id,
        credit_note_number: credit_note_number,
        credit_type: creditNote.credit_type,
        reason: creditNote.reason,
        comments: creditNote.comments,
        at_fault: creditNote.at_fault,
        approved_by: creditNote.approved_by,
        requested_at: creditNote.requested_at,
        updated_by: currentUser.attributes.given_name + ' ' + currentUser.attributes.family_name,
        updated_at: new Date().toISOString(),
      };
      
      if (!creditNote.approved_by && originalCreditNote.approved_at) {
        updatedCreditNote.approved_at = null;
        updatedCreditNote.status = 'Pending';
      }
      
      if (creditNote.approved_by && !originalCreditNote.approved_at) {
        updatedCreditNote.approved_at = new Date().toISOString();
        updatedCreditNote.status = 'Approved';
      }
      
      // Update credit note with error checking
      const creditNoteResponse = await API.graphql(graphqlOperation(mutations.updateCreditNote, { input: updatedCreditNote }));
      
      // Check for GraphQL errors in credit note update
      if (creditNoteResponse.errors && creditNoteResponse.errors.length > 0) {
        console.error('Credit note update GraphQL errors:', creditNoteResponse.errors);
        throw new Error(`Credit note update failed: ${creditNoteResponse.errors[0].message}`);
      }
      
      // Update line items with proper error handling
      const lineItemUpdatePromises = originalLineItems.map(async (originalLineItem, index) => {
        try {
          const lineItem = lineItems.find(row => row.id === originalLineItem.id);
          
          if (!lineItem) {
            throw new Error(`Line item ${index + 1} not found in current line items`);
          }
          
          // Validate numeric fields
          if (lineItem.quantity_to_credit && isNaN(parseInt(lineItem.quantity_to_credit, 10))) {
            throw new Error(`Invalid quantity to credit: ${lineItem.quantity_to_credit}`);
          }
          
          const updatedLineItem = {
            id: originalLineItem.id,
            quantity_to_credit: lineItem.quantity_to_credit ? parseInt(lineItem.quantity_to_credit, 10) : 0,
            original_unit_price: lineItem.original_unit_price ? parseFloat(lineItem.original_unit_price) : 0,
            unit_price: lineItem.unit_price ? parseFloat(lineItem.unit_price) : 0,
          };
          
          // Only include use_by_date if it has a valid value
          const formattedDate = formatAWSDate(lineItem.use_by_date);
          if (formattedDate) {
            updatedLineItem.use_by_date = formattedDate;
          }
          
          const lineItemResponse = await API.graphql(graphqlOperation(mutations.updateLineItem, { input: updatedLineItem }));
          
          // Check for GraphQL errors in the response
          if (lineItemResponse.errors && lineItemResponse.errors.length > 0) {
            console.error('Line item update GraphQL errors:', lineItemResponse.errors);
            console.error('Failed line item input:', updatedLineItem);
            throw new Error(`Line item ${index + 1} (${lineItem.sku}): ${lineItemResponse.errors[0].message}`);
          }
          
          return lineItemResponse.data.updateLineItem;
        } catch (error) {
          console.error(`Error updating line item ${index + 1}:`, error);
          console.error('Line item data:', originalLineItem);
          throw new Error(`Failed to update line item ${index + 1} (${originalLineItem.sku}): ${error.message}`);
        }
      });
      
      // Wait for all line items to be updated
      await Promise.all(lineItemUpdatePromises);
      
      navigate('/', { replace: true });
    } catch (error) {
      console.log(error);
      addAlert(error.toString());
      setIsSaving(false);
      return;
    }
  }

  const deleteCreditNote = async () => {
    setIsDeleting(true);
    if(!window.confirm('Are you sure you want to delete this credit note?')) {
      setIsDeleting(false);
      return;
    }
    
    try {
      const deleteResponse = await API.graphql(graphqlOperation(mutations.deleteCreditNote, { input: { id: originalCreditNote.id }}));
      
      // Check for GraphQL errors
      if (deleteResponse.errors && deleteResponse.errors.length > 0) {
        console.error('Delete credit note GraphQL errors:', deleteResponse.errors);
        throw new Error(`Failed to delete credit note: ${deleteResponse.errors[0].message}`);
      }
      
      navigate('/');
    } catch (error) {
      console.log(error);
      addAlert(error.toString());
      setIsDeleting(false);
      return;
    }
  }

  const cancelCreditNote = async () => {
    navigate('/');
  }


  return (
    <Flex margin="0 auto" direction="column" gap="1rem">
      <Flex direction="column">
        <Heading level={2}>Edit Credit Note</Heading>
        {isLoading && <Alert variation="info">Loading...</Alert>}
        {alerts}
        {orders && <OrdersDataGrid orders={orders} lineItems={lineItems} />}
        {creditNote && <Flex className="responsive-flex">
          <LineItemsDataGrid lineItems={lineItems} onChange={lineItems => setLineItems(lineItems)} />
          <Flex direction="column">
            <Button variation="primary" isLoading={isSaving} loadingText="Updating..." onClick={() => updateCreditNote()}>
              Update credit note
            </Button>
            <Button variation="destructive" isLoading={isDeleting} loadingText="Deleting..." onClick={() => deleteCreditNote()}>
              Delete credit note
            </Button>
            <Button onClick={() => cancelCreditNote()}>
              Cancel
            </Button>
            <CreditNoteForm creditNote={creditNote} onChange={fields => setCreditNote(fields)} />
            <Heading level={4}>Attachments</Heading>
            <FileUploadDownload folder={folder} />
          </Flex>
        </Flex>}
      </Flex>
    </Flex>
  );
};

